import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate, Link as RouterLink } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Box, Breadcrumbs, Typography, Link } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DepartmentGroupPage from './departmentGroup/DepartmentGroupPage';
import DepartmentPage from './department/DepartmentPage';

const tabRoutes = [
  { label: 'Department Group', value: 'department-group', icon: GroupWorkIcon },
  { label: 'Department', value: 'department', icon: GroupWorkIcon },
];

const segmentLabels = {
  masters: 'Masters',
  department: 'Department',
  'department-group': 'Department Group',
  'department': 'Department',
};

const buildPath = (segments, idx) => '/' + segments.slice(0, idx + 1).join('/');

const DepartmentMasterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = tabRoutes.find(tab => location.pathname.split('/').pop() === tab.value)?.value || 'department-group';
  const handleChange = (event, newValue) => {
    if (newValue !== currentTab) {
      navigate(`/masters/department/${newValue}`);
    }
  };

  // Tab bar logic (future-proof for more tabs)
  const tabBarRef = useRef(null);
  const [tabWindowStart, setTabWindowStart] = useState(0);
  const TABS_VISIBLE = 6;
  const atStart = tabWindowStart === 0;
  const atEnd = tabWindowStart + TABS_VISIBLE >= tabRoutes.length;
  const handlePrev = () => { if (!atStart) setTabWindowStart(tabWindowStart - 1); };
  const handleNext = () => { if (!atEnd) setTabWindowStart(tabWindowStart + 1); };
  const handleTabClick = (idx, value) => { handleChange(null, value); };

  // Breadcrumbs
  const breadcrumbItems = [
    { label: 'Masters', to: '/masters' },
    { label: 'Department', to: '/masters/department' },
    { label: segmentLabels[currentTab] || 'Department Group', to: currentTab },
  ];

  return (
    <Layout>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: '#00C853' }} />}
        aria-label="breadcrumb"
        sx={{ mb: 2, mt: 1 }}
      >
        {breadcrumbItems.map((item, idx) =>
          idx === breadcrumbItems.length - 1 ? (
            <Typography
              key={item.to}
              sx={{ color: '#111', fontWeight: 700, fontSize: 18, letterSpacing: 1, fontFamily: 'inherit' }}
            >
              {item.label}
            </Typography>
          ) : (
            <Link
              key={item.to}
              component={RouterLink}
              to={item.to}
              underline="hover"
              sx={{ color: '#111', fontWeight: 500, fontSize: 16, letterSpacing: 1, fontFamily: 'inherit', transition: 'color 0.3s ease', '&:hover': { color: '#111', fontWeight: 600 } }}
            >
              {item.label}
            </Link>
          )
        )}
      </Breadcrumbs>
      {/* Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', position: 'relative', marginBottom: 16 }}>
        <button
          onClick={handlePrev}
          disabled={atStart}
          style={{
            width: 25,
            height: 25,
            minWidth: 25,
            minHeight: 25,
            background: '#00C853',
            border: 'none',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 10,
            margin: '0 4px',
            opacity: atStart ? 0.5 : 1,
            cursor: atStart ? 'not-allowed' : 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
          }}
          aria-label="Scroll left"
        >
          {'<'}
        </button>
        <div
          style={{
            display: 'flex',
            overflow: 'hidden',
            flex: 1,
            borderBottom: '1.5px solid #e5e7eb',
            minHeight: 56,
            paddingBottom: 2,
          }}
        >
          {tabRoutes.slice(tabWindowStart, tabWindowStart + TABS_VISIBLE).map((tab, idx) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.value}
                onClick={() => handleTabClick(idx + tabWindowStart, tab.value)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minWidth: 120,
                  minHeight: 56,
                  padding: '12px 24px',
                  marginRight: 8,
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  background: currentTab === tab.value ? '#E8F5E9' : 'transparent',
                  color: '#111',
                  fontWeight: currentTab === tab.value ? 700 : 500,
                  fontSize: 12,
                  letterSpacing: 0.5,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: 'none',
                  outline: 'none',
                  borderBottom: currentTab === tab.value ? '4px solid #00C853' : '4px solid transparent',
                }}
              >
                <span style={{ fontSize: 22, color: currentTab === tab.value ? '#00C853' : '#757575', marginRight: 30 }}><Icon /></span>
                {tab.label}
              </button>
            );
          })}
        </div>
        <button
          onClick={handleNext}
          disabled={atEnd}
          style={{
            width: 25,
            height: 25,
            minWidth: 25,
            minHeight: 25,
            marginRight:25,
            background: '#00C853',
            border: 'none',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 16,
            margin: '0 4px',
            opacity: atEnd ? 0.5 : 1,
            cursor: atEnd ? 'not-allowed' : 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
          }}
          aria-label="Scroll right"
        >
          {'>'}
        </button>
      </div>
      {/* Tab Content */}
      <Routes>
        <Route index element={<Navigate to="department-group" />} />
        <Route path="department-group" element={<DepartmentGroupPage />} />
        <Route path="department" element={<DepartmentPage />} />
        <Route path="*" element={<div>Not Found: {location.pathname}</div>} />
      </Routes>
    </Layout>
  );
};

export default DepartmentMasterPage; 